import { useState, useEffect } from 'react'
import {
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle,
  XCircle,
  FileText,
  Globe,
  Webhook,
  Award,
  ArrowUpRight,
  AlertCircle,
  PlusIcon,
  Target,
  Loader2,
  Star,
  Search,
  Megaphone,
  BarChart3
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { toast } from 'react-toastify'
import AddCompetitorModal from '../components/AddCompetitorModal'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import { demoCompetitors, demoCompetitorScores } from '../utils/demoData'
import { trackEvent, ANALYTICS_EVENTS } from '../lib/analytics'
import { CompetitorAdsTab } from '../components/competitive-ads'

interface Competitor {
  id: string
  name: string
  domain: string
  category?: string
  description?: string
  addedAt: string
  isVerified: boolean
  recentMentions: number
  isHeadToHead: boolean
  shareOfVoice?: number
  sentimentScore?: number
  favorablePercent?: number
  neutralPercent?: number
  unfavorablePercent?: number
  competitiveRank?: number
}

interface CompetitorScore {
  id: string
  name: string
  domain: string
  aiIndexScore: number
  mentionRate: number
  avgPosition: number | null
  hasLlms: boolean
  hasManifest: boolean
  hasWebhooks: boolean
  trend: 'up' | 'down' | 'stable'
  scoreChange: number
  queriesAnalyzed: number
  totalMentions: number
  recentMentions: number
  lastUpdated: string
  shareOfVoice?: number
  sentimentScore?: number
  favorable?: number
  neutral?: number
  unfavorable?: number
}

const TrendIcon = ({ trend }: { trend: 'up' | 'down' | 'stable' }) => {
  if (trend === 'up') return <TrendingUp className="h-4 w-4 text-green-500" />
  if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-500" />
  return <Minus className="h-4 w-4 text-gray-400" />
}

const FeatureBadge = ({
  enabled,
  icon: Icon,
  label
}: {
  enabled: boolean
  icon: any
  label: string
}) => (
  <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
    enabled
      ? 'bg-green-100 text-green-700'
      : 'bg-gray-100 text-gray-500'
  }`}>
    {enabled ? (
      <CheckCircle className="h-3 w-3" />
    ) : (
      <XCircle className="h-3 w-3" />
    )}
    <Icon className="h-3 w-3" />
    <span>{label}</span>
  </div>
)

// TODO: Add Competitor Analysis Insights Section
// Similar to BrandPerformanceCard insights, but focused on:
// - Which competitors to study and learn from (and why)
// - Specific competitive advantages to exploit
// - Gap analysis and catch-up strategies
// Example recommendations:
// - "Competitor A has 2x your mention rate - they dominate Category X, invest there"
// - "You outperform Competitor B on technical queries - lean into this strength"
// - "All competitors have content on Topic Y, you don't - critical gap to fill"
// See: docs/AI_INSIGHTS_SYSTEM.md (Section-Specific Insights > Competitor Analysis)

type TabType = 'visibility' | 'ads'

export default function Competition() {
  const { activeBrand } = useAuth()
  const { isDemoMode } = useDemoMode()
  const [activeTab, setActiveTab] = useState<TabType>('visibility')
  const [competitors, setCompetitors] = useState<Competitor[]>([])
  const [competitorScores, setCompetitorScores] = useState<Map<string, CompetitorScore>>(new Map())
  const [loading, setLoading] = useState(true)
  const [loadingScores, setLoadingScores] = useState(false)
  const [sortBy, setSortBy] = useState<'score' | 'mentionRate' | 'shareOfVoice' | 'sentiment' | 'name'>('score')
  const [selectedCompetitor, setSelectedCompetitor] = useState<CompetitorScore | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [headToHeadLimit, setHeadToHeadLimit] = useState(3) // Default, updated based on tier
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (activeBrand?.id || isDemoMode) {
      fetchCompetitors()
      fetchHeadToHeadLimit()
    } else {
      // If there's no active brand and not in demo mode, stop loading
      setLoading(false)
    }
  }, [activeBrand?.id, isDemoMode])

  const fetchHeadToHeadLimit = async () => {
    if (isDemoMode) {
      setHeadToHeadLimit(6) // Demo shows GROWTH tier limit
      return
    }
    try {
      const response = await api.get(`/competitors/${activeBrand?.id}/head-to-head-limit`)
      if (response.data.success) {
        setHeadToHeadLimit(response.data.limit)
      }
    } catch (error) {
      console.error('Error fetching head-to-head limit:', error)
    }
  }

  const fetchCompetitors = async () => {
    try {
      setLoading(true)

      // Use demo data if in demo mode
      if (isDemoMode) {
        setCompetitors(demoCompetitors)
        await fetchAllScores(demoCompetitors)
        setLoading(false)
        return
      }

      const response = await api.get(`/competitors/${activeBrand?.id}`)
      setCompetitors(response.data.competitors || [])

      // Fetch scores for all competitors
      await fetchAllScores(response.data.competitors || [])
    } catch (error: any) {
      console.error('Error fetching competitors:', error)
      toast.error('Failed to load competitors')
    } finally {
      setLoading(false)
    }
  }

  const fetchAllScores = async (comps: Competitor[]) => {
    setLoadingScores(true)
    const scoresMap = new Map<string, CompetitorScore>()

    try {
      // Use demo data if in demo mode
      if (isDemoMode) {
        demoCompetitorScores.forEach((score: CompetitorScore) => {
          scoresMap.set(score.id, score)
        })
        setCompetitorScores(scoresMap)
        setLoadingScores(false)
        return
      }

      // Fetch all competitor scores in a single batch request
      if (comps.length > 0) {
        const response = await api.get(`/competitors/${activeBrand?.id}/scores`)

        if (response.data.success && response.data.scores) {
          response.data.scores.forEach((score: CompetitorScore) => {
            scoresMap.set(score.id, score)
          })
        }
      }

      setCompetitorScores(scoresMap)

    } catch (error) {
      console.error('Error fetching competitor scores:', error)
    } finally {
      setLoadingScores(false)
    }
  }

  const handleRemoveCompetitor = async (competitorId: string) => {
    if (!confirm('Are you sure you want to stop tracking this competitor?')) return

    try {
      await api.delete(`/competitors/${activeBrand?.id}/${competitorId}`)

      // Track competitor removed
      trackEvent(ANALYTICS_EVENTS.COMPETITOR_REMOVED, {
        competitorId
      })

      toast.success('Competitor removed')
      await fetchCompetitors()
    } catch (error: any) {
      toast.error('Failed to remove competitor')
    }
  }

  const handleToggleHeadToHead = async (competitorId: string, currentStatus: boolean) => {
    try {
      const response = await api.patch(`/competitors/${activeBrand?.id}/${competitorId}/head-to-head`, {
        isHeadToHead: !currentStatus
      })

      if (response.data.success) {
        toast.success(response.data.message)
        await fetchCompetitors()
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update head-to-head status'
      toast.error(message)
    }
  }

  const headToHeadCompetitors = competitors.filter(c => c.isHeadToHead)
  const headToHeadCount = headToHeadCompetitors.length

  const getCompetitorWithScore = (comp: Competitor): CompetitorScore | null => {
    return competitorScores.get(comp.id) || null
  }

  // Filter competitors by search query
  const filteredCompetitors = competitors.filter((comp) => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      comp.name.toLowerCase().includes(query) ||
      comp.domain.toLowerCase().includes(query) ||
      comp.category?.toLowerCase().includes(query)
    )
  })

  const sortedCompetitors = [...filteredCompetitors].sort((a, b) => {
    const scoreA = competitorScores.get(a.id)
    const scoreB = competitorScores.get(b.id)

    switch (sortBy) {
      case 'score':
        if (!scoreA || !scoreB) return 0
        return scoreB.aiIndexScore - scoreA.aiIndexScore
      case 'mentionRate':
        if (!scoreA || !scoreB) return 0
        return scoreB.mentionRate - scoreA.mentionRate
      case 'shareOfVoice':
        // Sort by Share of Voice (nulls last)
        const sovA = a.shareOfVoice ?? -1
        const sovB = b.shareOfVoice ?? -1
        return sovB - sovA
      case 'sentiment':
        // Sort by Sentiment Score (nulls last)
        const sentA = a.sentimentScore ?? -1
        const sentB = b.sentimentScore ?? -1
        return sentB - sentA
      case 'name':
        return a.name.localeCompare(b.name)
      default:
        return 0
    }
  })

  // Calculate summary stats
  const avgScore = competitors.length > 0
    ? Array.from(competitorScores.values()).reduce((sum, s) => sum + s.aiIndexScore, 0) / competitorScores.size
    : 0
  const withLlms = Array.from(competitorScores.values()).filter(s => s.hasLlms).length
  const withManifest = Array.from(competitorScores.values()).filter(s => s.hasManifest).length

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    )
  }

  if (!activeBrand && !isDemoMode) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No brand selected. Please select or create a brand to view competitive analysis.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-3xl font-bold text-gray-900">Competitive Analysis</h1>
          <p className="mt-1 lg:mt-2 text-sm lg:text-base text-gray-600">
            Track how competitors appear in AI assistant responses. Monitor their AI integration maturity and identify opportunities to improve your own visibility.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Add Competitor Button */}
          <div className="relative group">
            <button
              onClick={() => setShowAddModal(true)}
              disabled={isDemoMode}
              className={`px-4 py-2 text-sm text-nowrap font-medium rounded-lg transition-all ${
                isDemoMode
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                  : 'text-white bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              <PlusIcon className="h-4 w-4 inline mr-2" />
              Add Competitor
            </button>
            {isDemoMode && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                Available after sign up
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('visibility')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'visibility'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            AI Visibility
          </button>
          <button
            onClick={() => setActiveTab('ads')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'ads'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Megaphone className="h-4 w-4" />
            Advertising
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full">
              New
            </span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'ads' ? (
        <CompetitorAdsTab
          brandId={activeBrand?.id || ''}
          brand={activeBrand ? { id: activeBrand.id, name: activeBrand.companyName || activeBrand.name || '', domain: activeBrand.domain || '' } : undefined}
          competitors={competitors.map(c => ({ id: c.id, name: c.name, domain: c.domain, isHeadToHead: c.isHeadToHead }))}
          isDemoMode={isDemoMode}
        />
      ) : (
        <>
        {/* AI Visibility Tab Content - existing content */}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Tracked Competitors</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{competitors.length}</p>
            </div>
            <Target className="h-8 w-8 text-indigo-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Competitor Score</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {competitorScores.size > 0 ? avgScore.toFixed(1) : '—'}
              </p>
            </div>
            <Award className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">With LLMS.txt</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {withLlms}/{competitors.length}
              </p>
            </div>
            <FileText className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">With Manifest</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {withManifest}/{competitors.length}
              </p>
            </div>
            <Webhook className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Head-to-Head Competitors Section */}
      {headToHeadCount > 0 && (
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-600 fill-yellow-600" />
              <h3 className="text-lg font-semibold text-gray-900">Head-to-Head Competitors</h3>
              <span className="text-sm text-gray-600">({headToHeadCount}/{headToHeadLimit})</span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Your top competitors for direct comparison. Use the star icon to add up to {headToHeadLimit} competitors for focused analysis.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {headToHeadCompetitors.map((competitor) => {
              const score = getCompetitorWithScore(competitor)
              return (
                <div key={competitor.id} className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{competitor.name}</div>
                      <div className="text-xs text-gray-500 flex items-center mt-1">
                        <Globe className="h-3 w-3 mr-1" />
                        {competitor.domain}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleToggleHeadToHead(competitor.id, competitor.isHeadToHead)
                      }}
                      className="text-yellow-500 hover:text-yellow-600"
                      title="Remove from head-to-head"
                    >
                      <Star className="h-5 w-5 fill-yellow-500" />
                    </button>
                  </div>
                  {score && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">AI Index</span>
                        <span className="font-bold text-indigo-600">{score.aiIndexScore.toFixed(1)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-gray-600">Mention Rate</span>
                        <span className="font-semibold text-gray-900">{score.mentionRate.toFixed(1)}%</span>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {competitors.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Competitors Tracked</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Add competitors to track their AI integration status and see how they appear in AI assistant responses.
          </p>
          <div className="relative group inline-block">
            <button
              onClick={() => setShowAddModal(true)}
              disabled={isDemoMode}
              className={`px-6 py-3 text-sm font-medium rounded-lg transition-all ${
                isDemoMode
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                  : 'text-white bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              <PlusIcon className="h-4 w-4 inline mr-2" />
              Add Your First Competitor
            </button>
            {isDemoMode && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                Available after sign up
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Search and Sort Controls */}
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
              {/* Search Bar */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search competitors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="score">AI Index Score</option>
                    <option value="mentionRate">Mention Rate</option>
                    <option value="shareOfVoice">Share of Voice</option>
                    <option value="sentiment">Sentiment</option>
                    <option value="name">Name</option>
                  </select>
                </div>
                <span className="text-sm text-gray-500 whitespace-nowrap">
                  {searchQuery ? `${sortedCompetitors.length} of ` : ''}{competitors.length} competitor{competitors.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Competitors Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">

                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Competitor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      AI Index Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mention Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Share of Voice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sentiment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      AI Integration
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingScores && competitors.length > 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-8 text-center">
                        <Loader2 className="h-6 w-6 animate-spin text-indigo-600 mx-auto" />
                        <p className="text-sm text-gray-500 mt-2">Loading competitor scores...</p>
                      </td>
                    </tr>
                  ) : (
                    sortedCompetitors.map((competitor) => {
                      const score = getCompetitorWithScore(competitor)
                      const canAddHeadToHead = headToHeadCount < headToHeadLimit || competitor.isHeadToHead
                      return (
                        <tr
                          key={competitor.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => score && setSelectedCompetitor(score)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (canAddHeadToHead) {
                                    handleToggleHeadToHead(competitor.id, competitor.isHeadToHead)
                                  } else {
                                    toast.error(`Maximum of ${headToHeadLimit} head-to-head competitors allowed on your plan`)
                                  }
                                }}
                                className={`transition-colors ${
                                  competitor.isHeadToHead
                                    ? 'text-yellow-500 hover:text-yellow-600'
                                    : canAddHeadToHead
                                    ? 'text-gray-300 hover:text-yellow-400'
                                    : 'text-gray-200 cursor-not-allowed'
                                }`}
                                title={
                                  competitor.isHeadToHead
                                    ? 'Remove from head-to-head'
                                    : canAddHeadToHead
                                    ? 'Add to head-to-head'
                                    : 'Maximum head-to-head limit reached'
                                }
                              >
                                <Star className={`h-5 w-5 ${competitor.isHeadToHead ? 'fill-yellow-500' : ''}`} />
                              </button>
                              {/*
                              {competitor.competitiveRank && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  #{competitor.competitiveRank}
                                </span>
                              )}
                              */}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{competitor.name}</div>
                              <div className="text-sm text-gray-500 flex items-center">
                                <Globe className="h-3 w-3 mr-1" />
                                {competitor.domain}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {score ? (
                              <div className="flex items-center space-x-2">
                                <span className="text-2xl font-bold text-indigo-600">
                                  {score.aiIndexScore.toFixed(1)}
                                </span>
                                <div className="flex items-center space-x-1">
                                  <TrendIcon trend={score.trend} />
                                  {score.scoreChange !== 0 && (
                                    <span className={`text-xs font-medium ${
                                      score.trend === 'up' ? 'text-green-600' :
                                      score.trend === 'down' ? 'text-red-600' :
                                      'text-gray-500'
                                    }`}>
                                      {score.scoreChange > 0 ? '+' : ''}{score.scoreChange}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">Loading...</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {score ? (
                              <div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-lg font-semibold text-gray-900">{score.mentionRate.toFixed(1)}%</span>
                                </div>
                                <div className="text-xs text-gray-500">
                                  in {score.queriesAnalyzed} queries
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>

                          {/* Share of Voice */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {competitor.shareOfVoice !== null && competitor.shareOfVoice !== undefined ? (
                              <span className="text-lg font-semibold text-gray-900">
                                {competitor.shareOfVoice.toFixed(1)}%
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>

                          {/* Sentiment */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {competitor.sentimentScore !== null && competitor.sentimentScore !== undefined ? (
                              <div className="flex flex-col space-y-1">
                                <span className={`text-lg font-semibold ${
                                  competitor.sentimentScore >= 70 ? 'text-green-600' :
                                  competitor.sentimentScore >= 40 ? 'text-yellow-600' : 'text-red-600'
                                }`}>
                                  {competitor.sentimentScore.toFixed(0)}
                                </span>
                                {competitor.favorablePercent !== null && competitor.favorablePercent !== undefined && (
                                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                                    <span className="text-green-600">{competitor.favorablePercent.toFixed(0)}%</span>
                                    <span>/</span>
                                    <span className="text-gray-400">{competitor.neutralPercent?.toFixed(0) || 0}%</span>
                                    <span>/</span>
                                    <span className="text-red-600">{competitor.unfavorablePercent?.toFixed(0) || 0}%</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>

                          <td className="px-6 py-4">
                            {score ? (
                              <div className="flex flex-wrap gap-1">
                                <FeatureBadge enabled={score.hasLlms} icon={FileText} label="LLMS" />
                                <FeatureBadge enabled={score.hasManifest} icon={Globe} label="Manifest" />
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                score && setSelectedCompetitor(score)
                              }}
                              className="text-indigo-600 hover:text-indigo-900 inline-flex items-center mr-4"
                            >
                              View Details
                              <ArrowUpRight className="h-4 w-4 ml-1" />
                            </button>
                            <div className="relative group inline-block">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleRemoveCompetitor(competitor.id)
                                }}
                                disabled={isDemoMode}
                                className={`transition-all ${
                                  isDemoMode
                                    ? 'text-gray-400 cursor-not-allowed opacity-50'
                                    : 'text-red-600 hover:text-red-900'
                                }`}
                              >
                                Remove
                              </button>
                              {isDemoMode && (
                                <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                                  Available after sign up
                                  <div className="absolute top-full right-4 transform -mt-1 border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
        </>
      )}

      {/* Add Competitor Modal */}
      <AddCompetitorModal
        brandId={activeBrand?.id || ''}
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          toast.success('Competitor added successfully')
          fetchCompetitors()
        }}
      />

      {/* Detailed View Modal */}
      {selectedCompetitor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedCompetitor.name}</h2>
                  <a
                    href={`https://${selectedCompetitor.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center mt-1"
                  >
                    <Globe className="h-3 w-3 mr-1" />
                    {selectedCompetitor.domain}
                    <ArrowUpRight className="h-3 w-3 ml-1" />
                  </a>
                </div>
                <button
                  onClick={() => setSelectedCompetitor(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <div className="text-sm text-indigo-600 font-medium">AI Index Score</div>
                  <div className="flex items-baseline space-x-2 mt-1">
                    <span className="text-3xl font-bold text-indigo-900">
                      {selectedCompetitor.aiIndexScore.toFixed(1)}
                    </span>
                    <div className="flex items-center space-x-1">
                      <TrendIcon trend={selectedCompetitor.trend} />
                      {selectedCompetitor.scoreChange !== 0 && (
                        <span className={`text-sm font-medium ${
                          selectedCompetitor.trend === 'up' ? 'text-green-600' :
                          selectedCompetitor.trend === 'down' ? 'text-red-600' :
                          'text-gray-500'
                        }`}>
                          {selectedCompetitor.scoreChange > 0 ? '+' : ''}{selectedCompetitor.scoreChange}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-sm text-green-600 font-medium">Mention Rate</div>
                  <div className="text-3xl font-bold text-green-900 mt-1">
                    {selectedCompetitor.mentionRate.toFixed(1)}%
                  </div>
                  <div className="text-xs text-green-700 mt-1">
                    {selectedCompetitor.queriesAnalyzed} queries analyzed
                  </div>
                </div>
              </div>

              {/* AI Integration Features */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">AI Integration Status</h3>
                <div className="space-y-2">
                  <div className={`flex items-center justify-between p-3 rounded-lg ${
                    selectedCompetitor.hasLlms ? 'bg-green-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <FileText className={`h-5 w-5 ${
                        selectedCompetitor.hasLlms ? 'text-green-600' : 'text-gray-400'
                      }`} />
                      <span className="font-medium text-gray-900">LLMS.txt File</span>
                    </div>
                    {selectedCompetitor.hasLlms ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <div className={`flex items-center justify-between p-3 rounded-lg ${
                    selectedCompetitor.hasManifest ? 'bg-green-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <Globe className={`h-5 w-5 ${
                        selectedCompetitor.hasManifest ? 'text-green-600' : 'text-gray-400'
                      }`} />
                      <span className="font-medium text-gray-900">Manifest.json</span>
                    </div>
                    {selectedCompetitor.hasManifest ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  {/*
                  <div className={`flex items-center justify-between p-3 rounded-lg ${
                    selectedCompetitor.hasWebhooks ? 'bg-green-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <Webhook className={`h-5 w-5 ${
                        selectedCompetitor.hasWebhooks ? 'text-green-600' : 'text-gray-400'
                      }`} />
                      <span className="font-medium text-gray-900">Webhook Endpoints</span>
                    </div>
                    {selectedCompetitor.hasWebhooks ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  */}
                </div>

              </div>

              {/* Mention Stats */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Mention Statistics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm text-gray-600">Total Mentions</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">
                      {selectedCompetitor.totalMentions}
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm text-gray-600">Recent Mentions (7d)</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">
                      {selectedCompetitor.recentMentions}
                    </div>
                  </div>
                  {selectedCompetitor.avgPosition !== null && (
                    <div className="p-3 bg-gray-50 rounded-lg col-span-2">
                      <div className="text-sm text-gray-600">Average Position</div>
                      <div className="text-2xl font-bold text-gray-900 mt-1">
                        #{selectedCompetitor.avgPosition.toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        in AI responses
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <span className="text-xs text-gray-500">
                  Last updated: {new Date(selectedCompetitor.lastUpdated).toLocaleString()}
                </span>
                <button
                  onClick={() => setSelectedCompetitor(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
